import { Theme, ThemeOptions, createTheme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import { ComponentSizes } from './custom/sizeENUM';

/**
 * Custom factory function that behaves like `createTheme` but supports auto-merging
 * component-level themes from *.theme.ts files.
 *
 * This function:
 * - Creates a base theme using the provided options
 * - Dynamically imports all component theme files (*.theme.ts)
 * - Merges component overrides by passing the theme to each theme function
 * - Returns a fully extended Theme with all component customizations applied
 *
 * @param options - ThemeOptions to initialize the base theme
 * @param args - Additional objects to merge into the theme
 * @returns Theme - Fully extended MUI theme with component customizations
 */
export function extendMuiTheme(
  options?: ThemeOptions & { size?: ComponentSizes },
  ...args: object[]
): Theme {
  // Create base theme with provided options
  // @ts-expect-error - ThemeOptions type is complex and has overloads, but this works at runtime
  let theme = createTheme(options, ...args);

  // Add unit helper function to theme before loading component themes
  (theme as Theme & { unit: Theme['spacing'] }).unit = theme.spacing;

  // Add size configuration to theme before loading component themes
  (theme as Theme & { size: ComponentSizes }).size = options?.size || {};

  // Dynamically import all component theme functions
  const modules = import.meta.glob<{
    default: (theme: Theme) => ThemeOptions;
  }>('../components/**/**.theme.ts', { eager: true });

  // Apply each component theme function to extend the theme
  Object.values(modules).forEach((mod) => {
    const themeFn = mod.default;
    if (typeof themeFn === 'function') {
      const componentTheme = themeFn(theme);
      theme = deepmerge(theme, componentTheme, { clone: true });
    }
  });

  return theme;
}

/**
 * Type alias for the custom theme factory function
 */
export type ExtendMuiTheme = typeof extendMuiTheme;
